// TES-521 — the API answers with a machine code; the clinician reads Swedish.
//
// TES-811 — the table moved out of MeetingRoom and into lib/ because it is no
// longer only the video pane's: `clinician-api` needs it to turn a 403's code
// into words, and importing a component that pulls in daily-js just to read a
// lookup table would drag the whole call stack into every bundle that touches
// the API client.
const ERROR_TEXT: Record<string, string> = {
  // TES-1431 — the API's errors.cycle_decision.monthly_checkin_missing.
  // Words: Ö-790 (3), Emelie 2026-10-21, verbatim.
  monthly_checkin_missing:
    "Patientens månadsincheckning (F-05) är inte inskickad. Komplettera underlaget inför receptbedömningen. Behandlingen avslutas inte automatiskt.",
  too_early: "Mötet kan anslutas tidigast 24 timmar före starttiden.",
  // TES-521 — the mirror image: the server refuses a token once the
  // meeting is over or was swept to no_show / completed.
  meeting_over: "Mötet har redan avslutats och kan inte längre anslutas.",
  // TES-811 — Emelie read "Du saknar behörighet" and reported a permission
  // bug. It is not one: the meeting is simply booked on a colleague, and that
  // sends the reader somewhere completely different to look for the cause.
  not_your_meeting: "Det här vårdmötet är bokat på en annan läkare.",
  meeting_not_found: "Mötet kunde inte hittas.",
  meeting_cancelled: "Mötet är avbokat.",
  // TES-1383 — the join refusal for a meeting the patient moved; the meeting
  // view names the new time and links to it.
  meeting_rescheduled:
    "Patienten har bokat om mötet till en ny tid. Anslut till det nya mötet i stället.",
  // TES-534 — Art. 17: the person is gone, and so is the meeting.
  patient_erased:
    "Patienten har raderat sitt konto. Mötet är avslutat och kan inte dokumenteras.",
  // TES-811 — a 409 from the patient memory: two coaches corrected the same
  // row, or the coach's tab is showing a superseded one. Same wording as the
  // API's own `errors.coach_memory.not_active`, so the app and the portal do
  // not describe the same collision in two different ways.
  coach_memory_not_active:
    "Uppgiften har redan ändrats av någon annan. Ladda om och försök igen.",
  // TES-1237 — the structured Patientminne refuses identifiers and medical
  // content (TES-1325: whether AI drafts are on or off); the API's wording.
  coach_memory_forbidden_content:
    "Patientminnet får inte innehålla namn, platser, kontaktuppgifter eller medicinska uppgifter. Skriv om posten utan dem.",
  coach_memory_request_closed: "Begäran är redan hanterad.",
  // TES-1040 — an admin's read of a patient without today's support reason
  // (cancelled dialog, or the grant ran out at midnight).
  support_reason_required:
    "Ange en orsak för åtkomst för att se patientens uppgifter.",
  // TES-1060 — the completion note and the Webdoc lab action; the same
  // wording as the API's errors.json.
  journal_locked:
    "Journalanteckningen är låst eftersom den är införd i Webdoc och en senare anteckning (komplettering eller receptutfärdande) har påbörjats. Skriv det nya i den senare anteckningen.",
  completion_note_empty:
    "Kompletteringsanteckningen är tom. Skriv något innan du kopierar.",
  lab_order_already_reviewed: "Provsvaret är redan granskat.",
  // TES-1218 — the primary coach; the same wording as the API's errors.json.
  coach_unavailable: "Coachen är inte en aktiv coach i kliniken.",
  already_primary_coach: "Coachen är redan patientens coach.",
  coach_already_assigned: "Patienten har redan en coach.",
  lab_order_not_current_case:
    "Provbeställningen hör inte till det aktuella provärendet.",
  // TES-1284 — the same wording as the API's errors.json
  // (errors.labs.result_check_not_due, errors.labs.not_receivable).
  lab_result_check_not_due:
    "Det finns ingen provsvarskontroll att registrera just nu. Ladda om sidan.",
  lab_order_not_receivable:
    "Provsvaren kan inte markeras som inkomna för den här beställningen.",
  // TES-1073 (Ö-297) — the hard block on "Ella Underhåll"; the same wording as
  // the API's errors.json.
  underhall_not_eligible:
    "Ella Underhåll kan inte skickas: patienten har inte minst tolv månaders dokumenterad läkemedelsbehandling. Registrera en tidigare behandlingsstart eller välj Ella Uppstart.",
  no_completion_round:
    "Det finns ingen pågående komplettering att spara provbedömningen i.",
  // TES-1070 — "Bekräfta recept och publicera behandlingen"; the same
  // wording as the API's errors.json (errors.treatment_start.*).
  subscription_not_activated:
    "Receptet kan inte bekräftas förrän patientens abonnemang är aktiverat och betalningen verifierad.",
  prescription_note_not_in_webdoc:
    "Bekräfta att receptanteckningen är införd i Webdoc innan du publicerar behandlingen.",
  // TES-1150 — the step is open from the published positive decision.
  not_awaiting_prescription:
    "Receptstatus kan bara bekräftas efter ett publicerat positivt behandlingsbeslut.",
  no_planned_order:
    "Det finns ingen planerad ordination att bekräfta. Kontrollera läkemedel, dos och frekvens i beslutet.",
  clinical_phase_missing:
    "Behandlingsfasen saknas i beslutet. Välj fas innan behandlingen publiceras.",
  // TES-1080 — the prescription note behind the tick.
  prescription_note_not_confirmed:
    "Kopiera receptanteckningen och markera den som införd i Webdoc innan du publicerar behandlingen.",
  prescription_note_changed:
    "Receptanteckningen har ändrats sedan den markerades som införd i Webdoc. Kopiera den igen och bekräfta på nytt.",
  // TES-1061 — the treatment decision's journal note behind the tick; the
  // same wording as the API's errors.json (errors.clinician.decision_note_*).
  decision_note_not_copied:
    "Kopiera journalanteckningen med beslutet först, för in den i Webdoc och bekräfta sedan.",
  decision_note_changed:
    "Journalanteckningen har ändrats sedan den kopierades. Kopiera den igen, för in den i Webdoc och bekräfta igen.",
  concurrent_transition: "Något ändrades samtidigt. Försök igen.",
  // TES-442 — the LabPortalen quarantine; the same wording as the API's
  // errors.json (errors.labportalen.*, errors.labs.order_not_found).
  quarantine_not_found: "Provsvaret finns inte i karantänen.",
  quarantine_already_resolved: "Provsvaret är redan hanterat.",
  quarantine_not_attachable: "Provsvaret saknar innehåll som kan kopplas.",
  no_known_analytes:
    "Provsvaret innehåller inga analyser som Ella känner igen.",
  lab_order_not_found: "Provbeställningen kunde inte hittas.",
  // TES-1263 — the analyte mappings; the same wording as the API's
  // errors.json (errors.labportalen.mapping_* and neighbours).
  mapping_validation_failed:
    "Ange kod eller namn, enhet, analys och en orsak (minst 3 tecken).",
  unknown_analyte: "Analysen finns inte i provpanelen.",
  unit_needs_factor:
    "Enheten skiljer sig från analysens enhet. Ange en omräkningsfaktor.",
  factor_same_unit:
    "Enheten är densamma som analysens. Ta bort omräkningsfaktorn.",
  mapping_exists:
    "Det finns redan en aktiv koppling för koden eller namnet med samma enhet.",
  mapping_not_found: "Kopplingen finns inte.",
  mapping_inactive: "Kopplingen är redan inaktiverad.",
  // TES-1168 — "Boka nytt vårdmöte"; the same wording as the API's
  // errors.json (errors.booking.*).
  slot_taken: "Tiden är redan bokad. Välj en annan tid.",
  start_in_past: "Tiden har redan passerat. Välj en tid framåt.",
  doctor_not_found: "Läkaren kunde inte hittas.",
  validation_failed: "Bokningen kunde inte valideras. Kontrollera uppgifterna.",
  meeting_offer_not_found: "Vårdmötet kunde inte hittas.",
  meeting_offer_closed: "Det här vårdmötet går inte längre att bekräfta.",
  meeting_product_not_found: "Mötesprodukten finns inte.",
  // TES-1291 — the pause/resume/end publish; the API's errors.json wording.
  treatment_status_not_allowed:
    "Beslutet är inte tillåtet i behandlingens nuvarande status. Ladda om sidan och försök igen.",
  treatment_end_renewal_under_review:
    "En receptförnyelse granskas. Avgör den under Månadsincheckning innan behandlingen avslutas.",
  // TES-1554 — the end's reason; the API's errors.json wording.
  treatment_end_reason_required:
    "Välj anledning till att läkemedelsbehandlingen avslutas.",
  treatment_end_reason_not_enabled:
    "Anledning till avslutet kan inte anges ännu. Ladda om sidan och försök igen.",
  // TES-1291 — the medication switch; the API's errors.json wording.
  treatment_switch_renewal_under_review:
    "Patienten har begärt receptförnyelse. Välj det nya läkemedlet i förnyelsen under Månadsincheckning.",
  treatment_switch_same_medication:
    "Det är samma läkemedel som nu. En ny dos eller ett nytt intervall beslutas under Månadsincheckning.",
  // TES-1292 — the order's catalogue rules, as the switch publish meets them.
  medication_catalogue_version_unknown:
    "Läkemedelskatalogens post finns inte. Ladda om sidan och välj läkemedlet igen.",
  medication_deviation_reason_required:
    "Ordinationen avviker från den kliniskt godkända läkemedelskatalogen. Ange en motivering till avvikelsen.",
};

export function errorText(code: string): string {
  return ERROR_TEXT[code] ?? code;
}

// The copy a 403 has always shown. Still right for the role gate
// (`not_authorized`), which is what most 403s in the portal are.
export const FORBIDDEN_FALLBACK_TEXT =
  "Du saknar behörighet för den här sidan.";

// TES-811 — a 403 that names its cause gets that cause in words; one that
// does not (or names a code we have no copy for) keeps the generic line
// rather than putting a raw snake_case code in front of a doctor.
export function forbiddenText(code: string | null): string {
  return (code && ERROR_TEXT[code]) || FORBIDDEN_FALLBACK_TEXT;
}
